/**
 * @fileoverview Real `StudentTasksSource`. Loads the pendency spine, then
 * hydrates each card's labels from the detail endpoints in parallel (pooled,
 * resilient) caching every detail so the drawers reuse it with no extra fetch.
 */

import { invalidate, loadCached } from '../cache/taskDetailCache'
import { notifyStudentTasksUpdated } from '../events'
import { isDeliveryInProgress } from './adapters'
import {
  addDeliverableLink,
  fetchDeliverableDetail,
  fetchFinalAssessmentDetail,
  fetchPendencies,
  fetchSelfAssessmentDetail,
  removeDeliverableLink,
  submitDeliverable,
  submitFinalAssessment,
  submitSelfAssessment,
} from './studentTasksService'
import type {
  DeliverableDetail,
  FinalAssessmentDetail,
  ScoreInput,
  SelfAssessmentDetail,
  StudentTask,
  StudentTasksSource,
} from '../types'

const HYDRATION_TIMEOUT_MS = 8000
const keyOf = (type: StudentTask['type'], id: string) => `${type}:${id}`
/** Cache key for the pendency spine, shared by the list and the sidebar badge. */
const SPINE_KEY = 'pendencies:spine'

/** Rejects if the promise does not settle within `ms` (keeps the list smooth). */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('hydration timeout')), ms)),
  ])
}

/** Drops the cached spine and tells listeners (sidebar badge) to refetch. */
export function invalidateTasksSpine(): void {
  invalidate(SPINE_KEY)
  notifyStudentTasksUpdated()
}

const fetchDeliverable = (id: string) =>
  loadCached(keyOf('deliverable', id), () => fetchDeliverableDetail(id))
const fetchSelfAssessment = (id: string) =>
  loadCached(keyOf('self-assessment', id), () => fetchSelfAssessmentDetail(id))
const fetchFinalAssessment = (id: string) =>
  loadCached(keyOf('final-self-assessment', id), () => fetchFinalAssessmentDetail(id))

/** Fills a spine task's labels from its (cached) detail; degrades on failure. */
async function hydrate(task: StudentTask): Promise<StudentTask> {
  try {
    if (task.type === 'deliverable') {
      const d = await withTimeout(fetchDeliverable(task.identifierId), HYDRATION_TIMEOUT_MS)
      // `|| task.*` keeps the spine labels (AR-543) when the detail omits them.
      return { ...task, projectId: d.projectId, projectName: d.projectName || task.projectName,
        lessonTitle: d.lessonTitle || task.lessonTitle, lessonDate: d.lessonDate || task.lessonDate,
        title: d.title || task.title,
        deadline: d.deadline || task.deadline, overdue: d.overdue || task.overdue,
        // Started = open delivery with attached content (AR-873).
        inProgress: isDeliveryInProgress(d) }
    }
    if (task.type === 'self-assessment') {
      const d = await withTimeout(fetchSelfAssessment(task.identifierId), HYDRATION_TIMEOUT_MS)
      return { ...task, projectId: d.projectId, projectName: d.projectName || task.projectName,
        lessonTitle: d.title || task.lessonTitle, lessonDate: d.date || task.lessonDate }
    }
    // Final: project label already came from the pendency spine — no hydration
    // needed (avoids the slow final_assessments.json during list load).
    return task
  } catch (error) {
    console.error('Hydration failed for task', task.id, error)
    return task
  }
}

export const realSource: StudentTasksSource = {
  // Cached + in-flight-deduped so the list page and the sidebar badge that both
  // ask for the spine on the same page load share a single network sweep.
  fetchTasks: () => loadCached(SPINE_KEY, fetchPendencies),
  hydrateTask: hydrate,
  fetchDeliverable,
  fetchSelfAssessment,
  fetchFinalAssessment,
  // Mirrors the submit invalidation: after edit-mode file add/remove (no PUT)
  // the next drawer open/list load refetches the delivery fresh (AR-741).
  invalidateDeliverable(identifierId: string) {
    invalidate(keyOf('deliverable', identifierId))
    invalidate(SPINE_KEY)
  },
  async submitDeliverable(detail: DeliverableDetail, description: string) {
    await submitDeliverable(detail, description)
    invalidate(keyOf('deliverable', detail.identifierId))
    invalidateTasksSpine()
  },
  // Link add/remove (AR-677/AR-678): drop the cached detail (and spine) so the
  // next drawer open/list load refetches the delivery with the fresh links.
  async addDeliverableLink(detail: DeliverableDetail, url: string) {
    const link = await addDeliverableLink(detail, url)
    invalidate(keyOf('deliverable', detail.identifierId))
    invalidate(SPINE_KEY)
    return link
  },
  async removeDeliverableLink(detail: DeliverableDetail, linkId: string) {
    await removeDeliverableLink(detail, linkId)
    invalidate(keyOf('deliverable', detail.identifierId))
    invalidate(SPINE_KEY)
  },
  async submitSelfAssessment(detail: SelfAssessmentDetail, scores: readonly ScoreInput[], journal: string) {
    await submitSelfAssessment(detail, scores, journal)
    invalidate(keyOf('self-assessment', detail.identifierId))
    invalidateTasksSpine()
  },
  async submitFinalAssessment(detail: FinalAssessmentDetail, scores: readonly ScoreInput[], journal: string) {
    await submitFinalAssessment(detail, scores, journal)
    invalidate(keyOf('final-self-assessment', detail.identifierId))
    invalidateTasksSpine()
  },
}
